// client/src/routes/PmOnlyRoute.tsx
import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useAuth } from '@/contexts/AuthContext';

interface Props {
  children: React.ReactNode;
}

/**
 * Inner guard for PM-only routes nested under the PM/Supervisor route group.
 * Assumes the outer RoleProtectedRoute has already confirmed authentication
 * and at least one of { property_manager, security_supervisor }.
 *
 * If the user lacks property_manager, redirects to /manager and fires a
 * one-shot toast notifying them of the access boundary.
 */
export function PmOnlyRoute({ children }: Props): React.JSX.Element {
  const { user } = useAuth();
  const hasPm = user?.propertyRoles.some((r) => r.role === 'property_manager') ?? false;
  const denied = Boolean(user) && !hasPm;

  useEffect(() => {
    if (denied) {
      toast.info("You don't have access to that page.");
    }
  }, [denied]);

  if (denied) return <Navigate to="/manager" replace />;
  return <>{children}</>;
}
